import { NgModule } from "@angular/core";
import { PreloadAllModules, RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "src/guards/auth-guard";
import { HomepageGuard } from "src/guards/homepage-guard";
import { IntermediateRedirectGuard } from "src/guards/intermediate-redirect.guard";
import { PageDeactivationGuard } from "src/guards/page-deactivate.guard";
import { PermissionGuard } from "src/guards/permission-guard";
import { SplashGuardGuard } from "src/guards/splash-guard.guard";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { environment } from "../environments/environment";
//
const routes: Routes = [
  {
    path: "",
    pathMatch: "full",
    redirectTo: "/splash",
  },
  {
    path: "splash",
    canActivate: [SplashGuardGuard],
    loadChildren: () =>
      import("../pages/splash/splash.module").then((m) => m.SplashPageModule),
  },
  {
    path: "login",
    canLoad: [HomepageGuard],
    loadChildren: () =>
      import("../pages/login/login.module").then((m) => m.LoginPageModule),
  },
  {
    path: "create-account",
    canLoad: [HomepageGuard],
    loadChildren: () =>
      import("../pages/create-account/create-account.module").then(
        (m) => m.CreateAccountPageModule,
      ),
  },
  {
    path: "request-change-pwd",
    canLoad: [HomepageGuard],
    loadChildren: () =>
      import("../pages/request-change-pwd/request-change-pwd.module").then(
        (m) => m.RequestChangePwdPageModule,
      ),
  },
  {
    path: "policy",
    loadChildren: () =>
      import("../pages/policy/policy.module").then((m) => m.PolicyPageModule),
  },
  {
    path: "privacy",
    loadChildren: () =>
      import("../pages/privacy/privacy.module").then(
        (m) => m.PrivacyPageModule,
      ),
  },
  {
    path: "general-conditions",
    loadChildren: () =>
      import("../pages/general-conditions/general-conditions.module").then(
        (m) => m.GeneralConditionsPageModule,
      ),
  },
  {
    path: "privacy-policy",
    loadChildren: () =>
      import("../pages/privacy-policy/privacy-policy.module").then(
        (m) => m.PrivacyPolicyPageModule,
      ),
  },
  {
    path: "cookie-policy",
    loadChildren: () =>
      import("../pages/cookie-policy/cookie-policy.module").then(
        (m) => m.CookiePolicyPageModule,
      ),
  },

  {
    path: "reset-password",
    children: [
      {
        path: "",
        canActivate: [SplashGuardGuard],
        canDeactivate: [PageDeactivationGuard],
        pathMatch: "full",
        loadChildren: () =>
          import("../pages/reset-password/reset-password.module").then(
            (m) => m.ResetPasswordPageModule,
          ),
      },
    ],
  },
  {
    path: "dpa",
    loadChildren: () =>
      import("../pages/dpa/dpa.module").then((m) => m.DPAPageModule),
  },
  {
    path: "confirm-registration",
    children: [
      {
        path: "",
        canActivate: [SplashGuardGuard],
        canDeactivate: [PageDeactivationGuard],
        pathMatch: "full",
        loadChildren: () =>
          import("../pages/registration-confirmation/registration-confirmation.module").then(
            (m) => m.RegistrationConfirmationPageModule,
          ),
      },
    ],
  },
  {
    path: "reserved-area",
    children: [
      {
        path: "",
        redirectTo: AppSettings.URI_HOME_RESERVED_AREA,
        pathMatch: "full",
      },
      {
        path: "intermediate",
        canLoad: [AuthGuard, IntermediateRedirectGuard],
        loadChildren: () =>
          import("../pages/intermediate/intermediate.module").then(
            (m) => m.IntermediatePageModule,
          ),
      },
      {
        path: "settings",
        canDeactivate: [PageDeactivationGuard],
        children: [
          {
            path: "",
            loadChildren: () =>
              import("../pages/options/options.module").then(
                (m) => m.OptionsPageModule,
              ),
          },
        ],
      },
      {
        path: "accesses",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/accesses/accesses.module").then(
            (m) => m.AccessesPageModule,
          ),
      },
      {
        path: "supervisions",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/supervisions/supervisions.module").then(
                (m) => m.SupervisionsPageModule,
              ),
          },
          {
            path: "smartcomponents/:idEnvironment/graph",
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadComponent: () =>
              import("../components/shared-components/pages/graphic-supervision-smart-component/graphic-supervision-smart-component.page").then(
                (m) => m.GraphicSupervisionSmartComponentPage,
              ),
          },
          {
            path: ":idCustomInterface/:idEnvironment/graph",
            canLoad: [AuthGuard],
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/supervision-graphic/supervision-graphic.module").then(
                (m) => m.SupervisionGraphicPageModule,
              ),
          },

          {
            path: ":idCustomInterface/:idEnvironment/list",
            canLoad: [AuthGuard],
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/supervision-list/supervision-list.module").then(
                (m) => m.SupervisionListPageModule,
              ),
          },
          {
            path: "smartcomponents/:idEnvironment",
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadComponent: () =>
              import("../components/shared-components/pages/supervision-smart-component-details/supervision-smart-component-details.page").then(
                (m) => m.SupervisionSmartComponentDetailsPage,
              ),
          },
        ],
      },

      {
        path: "access-control",
        children: [
          {
            path: "",
            redirectTo: AppSettings.URI_HOME_RESERVED_AREA,
            pathMatch: "full",
          },
        ],
      },

      {
        path: "report",
        children: [
          {
            path: "",
            redirectTo: AppSettings.URI_HOME_RESERVED_AREA,
            pathMatch: "full",
          },
          {
            path: "report-payments",
            children: [
              {
                path: "",
                canLoad: [AuthGuard],
                canActivate: [PermissionGuard, AuthGuard],
                canDeactivate: [PageDeactivationGuard],

                loadChildren: () =>
                  import("../pages/report-payments/report-payments.module").then(
                    (m) => m.ReportPaymentsPageModule,
                  ),
              },
            ],
          },
          {
            path: "alarms-history",
            canLoad: [AuthGuard],
            canActivate: [PermissionGuard, AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/alarms-history/alarms-history.module").then(
                (m) => m.AlarmsHistoryPageModule,
              ),
          },
        ],
      },

      {
        path: "",
        redirectTo: `login`,
        pathMatch: "full",
      },
      {
        path: "home",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/home-dp/home-dp.module").then((m) => m.HomeDpModule),
      },
      {
        path: ":idArea/my-access",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/my-access/my-access.module").then(
            (m) => m.MyAccessPageModule,
          ),
      },
      {
        path: "smart-component-configuration",
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadComponent: () =>
          import("../components/shared-components/pages/smart-component-configuration/smart-component-configuration.page").then(
            (m) => m.SmartComponentConfigurationPage,
          ),
      },
      {
        path: "smart-component-excel-association",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/smart-component-excel-association/smart-component-excel-association.module").then(
            (m) => m.SmartComponentExcelAssociationPageModule,
          ),
      },
      {
        path: "associate-smart-component",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/associate-smart-component/associate-smart-component.module").then(
            (m) => m.AssociateSmartComponentPageModule,
          ),
      },

      {
        path: "invite-response",
        children: [
          {
            path: "",
            canActivate: [SplashGuardGuard],
            canDeactivate: [PageDeactivationGuard],
            pathMatch: "full",
            loadChildren: () =>
              import("../pages/invite-response/invite-response.module").then(
                (m) => m.InviteResponsePageModule,
              ),
          },
        ],
      },

      {
        path: "invitations-awaiting",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            pathMatch: "full",
            loadChildren: () =>
              import("../pages/invitations-awaiting/invitations-awaiting.module").then(
                (m) => m.InvitationsAwaitingPageModule,
              ),
          },
        ],
      },

      {
        path: "edit-building/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/edit-building/edit-building.module").then(
            (m) => m.EditBuildingPageModule,
          ),
      },
      /* Scenarios pages, from shared-components (standalone) */
      {
        path: "programming/scenarios",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadComponent: () =>
          import("src/components/shared-components/pages/scenarios/scenarios.page").then(
            (m) => m.ScenariosPage,
          ),
      },
      {
        path: "programming/scenarios/:idScenario",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadComponent: () =>
          import("src/components/shared-components/pages/scenarios-editor/scenarios-editor.page").then(
            (m) => m.ScenariosEditorPage,
          ),
      },
      /* Time-bands view of a weekly planning (one chart per week day). Must be declared BEFORE
       * "programming/weekly-planning/:id", otherwise "test-single-day" would match as ":id".
       * Nested (":id" as child) to mirror the webapp reference: the page reads the id from the
       * parent route's paramMap. */
      {
        path: "programming/weekly-planning/test-single-day",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        children: [
          {
            path: ":id",
            loadChildren: () =>
              import(
                "../pages/weekly-planning-test-single-day/weekly-planning-test-single-day.module"
              ).then((m) => m.WeeklyPlanningTestSingleDayPageModule),
          },
        ],
      },
      {
        path: "programming/weekly-planning/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/weekly-plannings-editor/weekly-plannings-editor.module").then(
            (m) => m.WeeklyPlanningsEditorPageModule,
          ),
      },
      {
        path: "weekly-planning/:idBuilding",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/weekly-planning/weekly-planning.module").then(
                (m) => m.WeeklyPlanningPageModule,
              ),
          },
        ],
      },
      {
        path: "edit-building/add/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/edit-building/edit-building.module").then(
            (m) => m.EditBuildingPageModule,
          ),
      },
      {
        path: "profile",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/user-profile/user-profile.module").then(
            (m) => m.UserProfilePageModule,
          ),
      },
      {
        path: "edit-environment/:idArea",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/edit-environment/edit-environment.module").then(
            (m) => m.EditEnvironmentPageModule,
          ),
      },
      {
        path: "edit-environment/:idArea/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/edit-environment/edit-environment.module").then(
            (m) => m.EditEnvironmentPageModule,
          ),
      },
      {
        path: "edit-building",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/edit-building/edit-building.module").then(
            (m) => m.EditBuildingPageModule,
          ),
      },

      {
        path: "invite-user/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/invite-user/invite-user.module").then(
            (m) => m.InviteUserPageModule,
          ),
      },

      {
        path: "view-invited-users/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/view-invited-users/view-invited-users.module").then(
            (m) => m.ViewInvitedUsersPageModule,
          ),
      },
      {
        path: "building-list",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/building-list/building-list.module").then(
            (m) => m.BuildingListPageModule,
          ),
      },
      {
        path: "trial-users",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/trial-users/trial-users.module").then(
            (m) => m.TrialUsersPageModule,
          ),
      },
      {
        path: "expenses-note",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/list-expense-note/list-expense-note.module").then(
            (m) => m.ListExpenseNotePageModule,
          ),
      },
      {
        path: "manage-expenses-note",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/expense-note-manage/expense-note-manage.module").then(
            (m) => m.ExpenseNoteManagePageModule,
          ),
      },

      {
        path: "list-bills",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/list-bills/list-bills.module").then(
                (m) => m.ListBillsPageModule,
              ),
          },
          {
            path: "bills-details",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/bills-details-edit/bills-details-edit.module").then(
                (m) => m.BillsDetailsEditPageModule,
              ),
          },
        ],
      },
      {
        path: "list-residents",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/list-residents/list-residents.module").then(
                (m) => m.ListResidentsPageModule,
              ),
          },
          {
            path: "residents-details",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/residents-details-edit/residents-details-edit.module").then(
                (m) => m.ResidentsDetailsEditPageModule,
              ),
          },
        ],
      },
      {
        path: "environments/:id",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/environments/environments.module").then(
            (m) => m.EnvironmentsPageModule,
          ),
      },
      {
        path: "environments/:idArea/:idEnvironment/import-readings",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/import-readings/import-readings.module").then(
            (m) => m.ImportReadingsPageModule,
          ),
      },
      {
        path: "environments/:id/associate-maintainers-system",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/environment-associate-maintainer-system/environment-associate-maintainer-system.module").then(
            (m) => m.EnvironmentAssociateMaintainerSystemModule,
          ),
      },
      {
        path: "manage-devices/mqtt-configuration/:idDevice",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/configuration-mqtt/configuration-mqtt.module").then(
            (m) => m.ConfigurationMqttPageModule,
          ),
      },
      {
        path: "manage-devices/:idArea/:idEnvironment",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/environment-manage-devices/environment-manage-devices.module").then(
            (m) => m.EnvironmentManageDeviceModule,
          ),
      },
      {
        path: "manage-devices/:idArea/:idEnvironment/associate-devices",
        canLoad: [AuthGuard],
        canActivate: [PermissionGuard, AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/associate-devices/associate-devices.module").then(
            (m) => m.AssociateDevicesPageModule,
          ),
      },
      {
        path: "access-management/:idArea/:idEnvironment",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/access-management/access-management.module").then(
            (m) => m.AccessManagementPageModule,
          ),
      },
      {
        path: "list-gateway/:idArea",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/list-gateway/list-gateway.module").then(
            (m) => m.ListGatewayModule,
          ),
      },

      {
        path: "list-quotes",
        children: [
          {
            path: "",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/list-quotes/list-quotes.module").then(
                (m) => m.ListQuotesPageModule,
              ),
          },
          {
            path: "quotes-details",
            canLoad: [AuthGuard],
            canActivate: [AuthGuard],
            canDeactivate: [PageDeactivationGuard],
            loadChildren: () =>
              import("../pages/quotes-details-edit/quotes-details-edit.module").then(
                (m) => m.QuotesDetailsEditPageModule,
              ),
          },
        ],
      },
      {
        path: "consumption-management",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/consumption-management/consumption-management.module").then(
            (m) => m.ConsumptionManagementModule,
          ),
      },
      {
        path: "document-security",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/document-security/document-security.module").then(
            (m) => m.DocumentSecurityPageModule,
          ),
      },
      {
        path: "voting",
        canLoad: [AuthGuard],
        canActivate: [AuthGuard],
        canDeactivate: [PageDeactivationGuard],
        loadChildren: () =>
          import("../pages/voting/voting.module").then(
            (m) => m.VotingPageModule,
          ),
      },
    ],
  },
  {
    path: "**", //DON'T TOUCH FROM IT
    redirectTo: "/splash",
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      // relativeLinkResolution: 'legacy', <-- rimuovi questa riga
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
  constructor() {}
}
